import { IPosition } from 'src/generated-types'
import { ConfigurePalette } from './configurePalette'
import {
  SelectionRect,
  boxOverlapsRect,
  isStraightLinePath,
  pathHitsRect,
  segmentHitsRect,
} from './drawingGeometry.action'

export const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'
export const SHAPE_TAGS = new Set(['path', 'line', 'rect', 'circle', 'polygon', 'polyline'])
export const HOVER_CLASS = 'cfg-hov'
const MARQUEE_ID = 'cfg-marquee'
const ANCHOR_MAX_RADIUS = 12

export const tagOf = (element: Element) => element.tagName.toLowerCase()

export const isAnchorCircle = (element: Element) =>
  tagOf(element) === 'circle' && Number(element.getAttribute('r') || 0) <= ANCHOR_MAX_RADIUS

export const boundsOf = (element: SVGGraphicsElement) => {
  try {
    return element.getBBox()
  } catch {
    return null
  }
}

export const toSvgPoint = (svg: SVGSVGElement, clientX: number, clientY: number) => {
  const point = svg.createSVGPoint()
  point.x = clientX
  point.y = clientY
  const screenMatrix = svg.getScreenCTM()
  if (!screenMatrix) return null
  return point.matrixTransform(screenMatrix.inverse())
}

export const shapeHitsRect = (element: SVGGraphicsElement, rect: SelectionRect) => {
  const tag = tagOf(element)
  if (tag === 'line') {
    const coordinate = (name: string) => Number(element.getAttribute(name))
    return segmentHitsRect(coordinate('x1'), coordinate('y1'), coordinate('x2'), coordinate('y2'), rect)
  }
  if (tag === 'path') {
    const pathData = element.getAttribute('d')
    if (pathData && isStraightLinePath(pathData)) return pathHitsRect(pathData, rect)
  }
  const bounds = boundsOf(element)
  return !!bounds && boxOverlapsRect(bounds, rect)
}

const belongsToOtherPosition = (element: Element, positionId: string) => {
  const markedId = element.getAttribute('data-position-id')
  if (markedId && markedId !== positionId) return true
  if (!element.id || !element.id.startsWith('position')) return false
  return (
    element.id !== `positionId-${positionId}` &&
    element.id !== `positionTextId-${positionId}` &&
    element.id !== `positionTextContainerId-${positionId}`
  )
}

// Finds every SVG node rendered for `position` and tags it with `data-cfg-id` / `data-cfg-type`
export const markPosition = (svg: SVGSVGElement, position: IPosition) => {
  const positionId = String(position.id)
  const escapedId = CSS.escape(positionId)
  const nodes = new Set<Element>()
  const addNode = (node: Element | null | undefined) => {
    if (node) nodes.add(node)
  }
  svg.querySelectorAll(`[data-position-id="${positionId}"]`).forEach(addNode)
  addNode(svg.querySelector(`#positionId-${escapedId}`))
  const labelText = svg.querySelector(`#positionTextId-${escapedId}`)
  addNode(labelText)
  addNode(svg.querySelector(`#positionTextContainerId-${escapedId}`))
  if (
    labelText &&
    tagOf(labelText) === 'textpath' &&
    labelText.parentElement &&
    tagOf(labelText.parentElement) === 'text'
  ) {
    addNode(labelText.parentElement)
  }

  let previous = labelText?.previousElementSibling ?? null
  let steps = 0
  while (previous && steps < 3) {
    if (belongsToOtherPosition(previous, positionId) || !SHAPE_TAGS.has(tagOf(previous))) break
    const ownsAnchor =
      !previous.id || previous.id === String(position.name) || previous.id === `positionId-${positionId}`
    if (!ownsAnchor) break
    addNode(previous)
    if (previous.id === `positionId-${positionId}`) break
    previous = previous.previousElementSibling
    steps += 1
  }

  nodes.forEach(node => {
    node.setAttribute('data-cfg-id', positionId)
    node.setAttribute('data-cfg-type', String(position.type))
  })
  return [...nodes]
}

export const ensureMarquee = (svg: SVGSVGElement, palette: ConfigurePalette) => {
  const existing = svg.querySelector<SVGRectElement>(`#${MARQUEE_ID}`)
  if (existing) return existing
  const marquee = document.createElementNS(SVG_NAMESPACE, 'rect')
  marquee.id = MARQUEE_ID
  marquee.setAttribute('fill', palette.marqueeFill)
  marquee.setAttribute('stroke', palette.primary)
  marquee.setAttribute('stroke-width', '1.5')
  marquee.setAttribute('stroke-dasharray', '6 4')
  marquee.setAttribute('pointer-events', 'none')
  marquee.style.display = 'none'
  svg.appendChild(marquee)
  return marquee
}

export const showMarquee = (
  marquee: SVGRectElement,
  rect: SelectionRect,
  removing: boolean,
  palette: ConfigurePalette,
) => {
  marquee.setAttribute('x', String(rect.left))
  marquee.setAttribute('y', String(rect.top))
  marquee.setAttribute('width', String(rect.right - rect.left))
  marquee.setAttribute('height', String(rect.bottom - rect.top))
  marquee.style.display = ''
  marquee.setAttribute('stroke', removing ? palette.error : palette.primary)
  marquee.setAttribute('fill', removing ? palette.marqueeRemoveFill : palette.marqueeFill)
}

export const setHoveredPosition = (host: HTMLElement, positionId: string | null) => {
  host.querySelectorAll(`.${HOVER_CLASS}`).forEach(node => node.classList.remove(HOVER_CLASS))
  if (positionId)
    host.querySelectorAll(`[data-cfg-id="${positionId}"]`).forEach(node => node.classList.add(HOVER_CLASS))
}
